import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { shareReplay, tap, catchError } from 'rxjs/operators';
import { environment } from '../../../environments/environment';

export interface NavigationTab {
  id: number;
  name: string;
  description?: string;
  isActive: boolean;
  order: number;
  categoryIds?: number[];
  parentId?: number;
  imageUrl?: string;
  parent?: NavigationTab;
  children?: NavigationTab[];
  createdAt: string;
  updatedAt: string;
}

@Injectable({
  providedIn: 'root'
})
export class NavigationTabsApiService {
  private apiUrl = `${environment.baseUrl}/navigation-tabs`;
  private activeTabsCache$: Observable<NavigationTab[]> | null = null;
  private cacheTimestamp = 0;
  private readonly CACHE_DURATION = 5 * 60 * 1000; // 5 minutes
  private readonly LOCAL_STORAGE_KEY = 'active_navigation_tabs_cache_v1';
  private readonly LOCAL_STORAGE_TS_KEY = 'active_navigation_tabs_cache_ts_v1';

  constructor(private http: HttpClient) {}

  getActiveNavigationTabs(): Observable<NavigationTab[]> {
    // Check for cached data in localStorage first
    const localTabsJson = this.safeLocalStorageGet(this.LOCAL_STORAGE_KEY);
    const localTabsTsStr = this.safeLocalStorageGet(this.LOCAL_STORAGE_TS_KEY);
    const localTs = localTabsTsStr ? Number(localTabsTsStr) : 0;

    // If we have fresh local cache, return it immediately
    if (localTabsJson && localTs && Date.now() - localTs < this.CACHE_DURATION) {
      try {
        const parsed: NavigationTab[] = JSON.parse(localTabsJson);
        if (Array.isArray(parsed) && parsed.length > 0) {
          return of(parsed);
        }
      } catch {}
    }

    // If we already have a valid in-memory cache, return it
    if (this.activeTabsCache$ && Date.now() - this.cacheTimestamp < this.CACHE_DURATION) {
      return this.activeTabsCache$;
    }

    // Otherwise, fetch fresh data
    this.activeTabsCache$ = this.http.get<NavigationTab[]>(`${this.apiUrl}/active`).pipe(
      tap((tabs) => {
        this.cacheTimestamp = Date.now();
        this.safeLocalStorageSet(this.LOCAL_STORAGE_KEY, JSON.stringify(tabs));
        this.safeLocalStorageSet(this.LOCAL_STORAGE_TS_KEY, String(this.cacheTimestamp));
      }),
      catchError(error => {
        this.invalidateCache();
        return of([]);
      }),
      shareReplay(1)
    );

    return this.activeTabsCache$;
  }

  getMainTabsWithChildren(): Observable<NavigationTab[]> {
    return this.http.get<NavigationTab[]>(`${this.apiUrl}/main-with-children`);
  }

  getAllNavigationTabs(): Observable<NavigationTab[]> {
    return this.http.get<NavigationTab[]>(this.apiUrl);
  }

  /**
   * Invalidate the cache - useful when data might have changed
   */
  invalidateCache(): void {
    this.activeTabsCache$ = null;
    this.cacheTimestamp = 0;
    this.safeLocalStorageRemove(this.LOCAL_STORAGE_KEY);
    this.safeLocalStorageRemove(this.LOCAL_STORAGE_TS_KEY);
  }

  private safeLocalStorageGet(key: string): string | null {
    try {
      return localStorage.getItem(key);
    } catch {
      return null;
    }
  }

  private safeLocalStorageSet(key: string, value: string): void {
    try {
      localStorage.setItem(key, value);
    } catch {
      // ignore
    }
  }

  private safeLocalStorageRemove(key: string): void {
    try {
      localStorage.removeItem(key);
    } catch {
      // ignore
    }
  }
}
